import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';

// WhatsApp Cloud API Configuration
export interface WhatsAppConfig {
  enabled: boolean;
  accessToken: string;
  phoneNumberId: string;
  businessAccountId?: string;
  channelId?: string;
  webhookVerifyToken?: string;
  lastUpdated?: Timestamp;
  updatedBy?: string;
}

export interface WhatsAppMessageResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

export interface AttachmentInfo {
  originalName: string;
  url: string;
}

// Default config
const DEFAULT_CONFIG: WhatsAppConfig = {
  enabled: false,
  accessToken: '',
  phoneNumberId: '',
  businessAccountId: '',
  channelId: '',
  webhookVerifyToken: '',
};

// ===========================
// 1. GET WHATSAPP CONFIG
// ===========================

export async function getWhatsAppConfig(domain: string): Promise<WhatsAppConfig> {
  try {
    const configRef = doc(db, `school-users/${domain}/settings`, 'whatsapp');
    const configDoc = await getDoc(configRef);

    if (configDoc.exists()) {
      return configDoc.data() as WhatsAppConfig;
    }

    return DEFAULT_CONFIG;
  } catch (error) {
    console.error('Error fetching WhatsApp config:', error);
    return DEFAULT_CONFIG;
  }
}

// ===========================
// 2. SAVE WHATSAPP CONFIG
// ===========================

export async function saveWhatsAppConfig(
  domain: string,
  config: Partial<WhatsAppConfig>,
  updatedBy: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const configRef = doc(db, `school-users/${domain}/settings`, 'whatsapp');

    await setDoc(configRef, {
      ...config,
      lastUpdated: Timestamp.now(),
      updatedBy,
    }, { merge: true });

    console.log('✅ WhatsApp config saved successfully');
    return { success: true };
  } catch (error: any) {
    console.error('Error saving WhatsApp config:', error);
    return { success: false, error: error.message };
  }
}

// ===========================
// 3. SEND MESSAGE TO WHATSAPP CHANNEL
// ===========================

export async function sendToWhatsAppChannel(
  domain: string,
  message: {
    title: string;
    content: string;
    category?: string;
    priority?: string;
    attachments?: AttachmentInfo[];
  }
): Promise<WhatsAppMessageResult> {
  try {
    // Get WhatsApp configuration
    const config = await getWhatsAppConfig(domain);

    if (!config.enabled) {
      return { success: false, error: 'WhatsApp integration is not enabled' };
    }

    if (!config.accessToken || !config.phoneNumberId) {
      return { success: false, error: 'WhatsApp configuration is incomplete' };
    }

    // Format the message for WhatsApp
    const formattedMessage = formatAnnouncementForWhatsApp(message);

    // If channel ID is provided, send to channel
    // Otherwise, this would need a recipient number (for direct messages)
    if (config.channelId) {
      // Send to WhatsApp Channel using Newsletter API
      const channelApiUrl = `https://graph.facebook.com/v18.0/${config.channelId}/messages`;

      const response = await fetch(channelApiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messaging_product: 'whatsapp',
          type: 'text',
          text: {
            body: formattedMessage,
          },
        }),
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ Message sent to WhatsApp Channel:', result);
        return { success: true, messageId: result.messages?.[0]?.id };
      } else {
        console.error('❌ WhatsApp API error:', result);
        return { success: false, error: result.error?.message || 'Failed to send message' };
      }
    } else {
      // For broadcast without channel, use template message or status broadcast
      // This requires Meta approval for template messages
      console.warn('⚠️ No channel ID configured. Direct broadcast requires approved templates.');

      // Alternative: Use WhatsApp Status/Story feature (if available)
      // For now, return guidance
      return {
        success: false,
        error: 'Channel ID not configured. Please set up a WhatsApp Channel or use approved message templates.',
      };
    }
  } catch (error: any) {
    console.error('Error sending to WhatsApp:', error);
    return { success: false, error: error.message };
  }
}

// ===========================
// 4. SEND TO WHATSAPP GROUP (Using Business API)
// ===========================

export async function sendToWhatsAppGroup(
  domain: string,
  _groupId: string,
  _message: {
    title: string;
    content: string;
    category?: string;
  }
): Promise<WhatsAppMessageResult> {
  try {
    const config = await getWhatsAppConfig(domain);

    if (!config.enabled || !config.accessToken) {
      return { success: false, error: 'WhatsApp not configured' };
    }

    // Note: WhatsApp Business API doesn't directly support group messaging
    // This would require WhatsApp Business Platform with group features
    // or using unofficial methods (not recommended)

    console.warn('⚠️ Direct group messaging requires WhatsApp Business Platform');
    return {
      success: false,
      error: 'Group messaging requires additional WhatsApp Business Platform setup',
    };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

// ===========================
// 5. FORMAT MESSAGE FOR WHATSAPP
// ===========================

function formatAnnouncementForWhatsApp(message: {
  title: string;
  content: string;
  category?: string;
  priority?: string;
  attachments?: AttachmentInfo[];
}): string {
  const categoryEmojis: Record<string, string> = {
    announcement: '📢',
    teacher_announcement: '👨‍🏫',
    fee: '💰',
    exam: '📝',
    assignment: '📚',
    result: '🏆',
  };

  const priorityIndicator: Record<string, string> = {
    high: '🔴 URGENT',
    medium: '🟡',
    low: '🟢',
  };

  const emoji = categoryEmojis[message.category || 'announcement'] || '📢';
  const priority = message.priority ? priorityIndicator[message.priority] || '' : '';

  let formattedMessage = `${emoji} *${message.title}*`;

  if (priority) {
    formattedMessage += ` ${priority}`;
  }

  formattedMessage += `\n\n${message.content}`;

  // Add attachments section if present
  if (message.attachments && message.attachments.length > 0) {
    formattedMessage += `\n\n📎 *Attachments:*`;
    message.attachments.forEach((attachment, index) => {
      formattedMessage += `\n${index + 1}. ${attachment.originalName}: ${attachment.url}`;
    });
  }

  formattedMessage += `\n\n---\n_Sent from MLB - My Learning Board_`;

  return formattedMessage;
}

// ===========================
// 6. GENERATE WHATSAPP SHARE LINK
// ===========================

export function generateWhatsAppShareLink(message: {
  title: string;
  content: string;
  category?: string;
  attachments?: AttachmentInfo[];
}): string {
  const formattedMessage = formatAnnouncementForWhatsApp(message);
  const encodedMessage = encodeURIComponent(formattedMessage);
  return `https://wa.me/?text=${encodedMessage}`;
}

// ===========================
// 7. GENERATE CHANNEL INVITE LINK
// ===========================

export function generateChannelInviteLink(channelUsername: string): string {
  return `https://whatsapp.com/channel/${channelUsername}`;
}

// ===========================
// 8. TEST WHATSAPP CONNECTION
// ===========================

export async function testWhatsAppConnection(
  accessToken: string,
  phoneNumberId: string
): Promise<{ success: boolean; error?: string; phoneNumber?: string }> {
  try {
    const response = await fetch(
      `https://graph.facebook.com/v18.0/${phoneNumberId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );

    const result = await response.json();

    if (response.ok) {
      return {
        success: true,
        phoneNumber: result.display_phone_number,
      };
    } else {
      return {
        success: false,
        error: result.error?.message || 'Connection test failed',
      };
    }
  } catch (error: any) {
    return { success: false, error: error.message };
  }
}

// ===========================
// 9. LOG WHATSAPP MESSAGE
// ===========================

export async function logWhatsAppMessage(
  domain: string,
  announcementId: string,
  result: WhatsAppMessageResult
): Promise<void> {
  try {
    const logRef = doc(db, `school-users/${domain}/whatsapp-logs`, `${announcementId}_${Date.now()}`);
    await setDoc(logRef, {
      announcementId,
      success: result.success,
      messageId: result.messageId || null,
      error: result.error || null,
      timestamp: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error logging WhatsApp message:', error);
  }
}
